import { useEffect, useState } from "react";
import { createRoute, useSearch } from "@tanstack/react-router";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import { Button, Empty, ErrorBlock, Input, Price, Row, Spinner, fenInt } from "../components/ui.tsx";
import { useCheckout } from "../lib/use-checkout.ts";
import { RegionPicker } from "../components/region-picker.tsx";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopCheckout } from "./desktop/checkout.tsx";

/**
 * 结算页（手机版）。
 *
 * ⚠️ 逻辑全在 `useCheckout()` 里，这里只管版面 —— 网页版是另一套版面、
 *    同一份逻辑。抄两份的话迟早走岔，而走岔的地方全是钱。
 */
function Checkout() {
  /** ⚠️ 地址栏带了 skuId 就是「立即下单」——只买这一件，不碰购物车。 */
  const { skuId, qty } = useSearch({ from: "/checkout" });
  const c = useCheckout(skuId ? { skuId, quantity: qty && qty > 0 ? qty : 1 } : null);
  const [couponInput, setCouponInput] = useState("");
  const [showForm, setShowForm] = useState(false);
  /** ⚠️ 一动表单就把「还差…」那条提示清掉，见 `use-checkout.ts`。 */
  const clearErr = c.clearAddressError;
  const [form, setForm] = useState({
    receiver: "",
    phone: "",
    province: "",
    city: "",
    district: "",
    detail: "",
  });

  /**
   * 🔴 **这个 effect 必须排在下面那几个 early return 之前。**
   *    React 的 hook 数量每次渲染必须一样多 —— 放在 return 后面的话，
   *    购物车还在查询的那一轮少跑一个，数据回来的那一轮又多出来，
   *    直接抛「Rendered more hooks than during the previous render」整页白屏。
   *    ⚠️ **而且它是概率性的**：查询命中缓存时根本不会有 pending 那一轮，
   *    页面就好好的 —— 所以本地点几次可能都碰不到，真机冷启动才炸。
   *    （这个坑本项目犯过不止一次，见 MEMORY「hook 必须全部排在 early return 之前」。）
   *
   * ⚠️ 作用：用户一动收货地址表单就把「还差…」那条提示清掉。
   */
  useEffect(() => {
    clearErr();
  }, [form, clearErr]);

  const { cart, addresses } = c;

  if (c.cartQuery.isPending) return <SubPage title="确认订单"><Spinner /></SubPage>;
  if (c.cartQuery.error)
    return <SubPage title="确认订单"><ErrorBlock error={c.cartQuery.error} /></SubPage>;
  if (!cart || cart.groups.length === 0) {
    return (
      <SubPage title="确认订单">
        <Empty title="购物车是空的" action={{ label: "去逛逛", to: "/products" }} />
      </SubPage>
    );
  }

  /**
   * 🔴 **地址还没保存时，提交要顺手把它存了，不能一声不吭地什么都不做。**
   *    以前是 `if (!c.addressId) { setShowForm(true); return; }` ——
   *    而一个地址都没有的时候表单**本来就是开着的**，于是用户把信息填齐、
   *    点「提交订单」，页面纹丝不动、也没有任何提示，看着就像按钮坏了。
   *    真机上被逮到的就是这一条。
   */
  const submit = async () => {
    setShowForm(true);
    await c.submit(form);
  };

  return (
    <SubPage
      title="确认订单"
      footer={
        <div className="space-y-2">
          {/*
            🔴 **提交失败的原因要贴在按钮旁边。** 以前地址没填全时这里
               什么都不显示，用户只会反复点那个按钮。
          */}
          {c.addressError && (
            <p className="text-[13px] text-destructive">{c.addressError}</p>
          )}
          <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="text-sm">
              应付 <Price cents={c.payable} className="text-lg text-brand-600" />
            </p>
            <p className="text-xs text-ink-500">
              含运费 <Price cents={cart.totalFreight} />
              {c.coupon && (
                <span className="ml-1.5 text-brand-600">
                  已减 <Price cents={c.coupon.discount} />
                </span>
              )}
            </p>
          </div>
          <Button size="lg" onClick={submit} loading={c.createOrder.isPending} disabled={c.blocked}>
            {c.blocked ? "有商品缺货" : "提交订单"}
          </Button>
          </div>
        </div>
      }
    >
      {/* ⚠️ 价格变动不是"出错"，是要用户再看一眼 —— 用提示色而不是报错色 */}
      {c.priceChanged && (
        <div className="mx-4 mt-2 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="font-medium">商品价格有变动，已为你更新</p>
          <p className="mt-0.5 text-xs">请确认下面的金额后再提交。</p>
        </div>
      )}
      {c.createOrder.error && !c.priceChanged && <ErrorBlock error={c.createOrder.error} />}

      <section className="bg-white px-4 py-3">
        <h2 className="mb-2 text-sm font-medium">收货地址</h2>

        {addresses && addresses.length > 0 && !showForm ? (
          <div className="space-y-2">
            {addresses.map((a) => (
              <button
                key={a.id}
                onClick={() => c.setAddressId(a.id)}
                className={`block w-full rounded-lg border px-3 py-2.5 text-left ${
                  c.addressId === a.id ? "border-brand-500 bg-brand-50" : "border-neutral-200"
                }`}
              >
                <p className="text-sm">
                  {a.receiver} <span className="ml-2 text-ink-500">{a.phone}</span>
                  {a.isDefault && (
                    <span className="ml-2 rounded bg-brand-100 px-1.5 py-0.5 text-xs text-brand-700">
                      默认
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-ink-500">
                  {a.province}
                  {a.city}
                  {a.district} {a.detail}
                </p>
              </button>
            ))}
            <button className="text-sm text-brand-600" onClick={() => setShowForm(true)}>
              + 新增地址
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="收货人"
                value={form.receiver}
                onChange={(e) => setForm({ ...form, receiver: e.target.value })}
              />
              <Input
                placeholder="手机号"
                inputMode="numeric"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <RegionPicker value={form} onChange={(r) => setForm({ ...form, ...r })} />
            </div>
            <Input
              placeholder="详细地址（含楼层，建材配送要用）"
              value={form.detail}
              onChange={(e) => setForm({ ...form, detail: e.target.value })}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                loading={c.saveAddress.isPending}
                onClick={async () => {
                  const saved = await c.saveAddress.mutateAsync({
                    ...form,
                    isDefault: !addresses || addresses.length === 0,
                  });
                  c.setAddressId(saved.id);
                  setShowForm(false);
                }}
              >
                保存
              </Button>
              {addresses && addresses.length > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>
                  取消
                </Button>
              )}
            </div>
            {c.saveAddress.error && <ErrorBlock error={c.saveAddress.error} />}
          </div>
        )}
      </section>

      {/* ⚠️ 按供应商分组展示，并明说会拆成几张单 —— 用户看到多个订单号时才不会以为重复下单了 */}
      {cart.groups.map((g) => (
        <section key={g.supplierOrgId} className="mt-2 bg-white">
          <h2 className="border-b border-neutral-100 px-4 py-2.5 text-sm text-ink-500">
            {g.supplierName}
          </h2>
          {g.lines.map((l) => (
            <div key={l.skuId} className="flex justify-between px-4 py-2.5 text-sm">
              <span className="min-w-0 flex-1 truncate pr-3">{l.title}</span>
              <span className="shrink-0 text-right text-ink-500">
                <Price cents={l.unitPrice} /> × {l.quantity}
                {/* ⚠️ 只在这一行真的有优惠时才划 —— 相等就不显示 */}
                {l.originalSubtotal > l.subtotal && (
                  <span className="ml-1.5 text-[11px] line-through opacity-60">
                    {fenInt(l.originalSubtotal)}
                  </span>
                )}
              </span>
            </div>
          ))}
          <div className="flex justify-between border-t border-neutral-100 px-4 py-2 text-sm">
            <span className="text-ink-500">运费</span>
            <Price cents={g.freightAmount} />
          </div>
        </section>
      ))}

      {/*
        🔴 **金额明细：商品原价 / 店铺优惠 / 运费 / 券 / 实付。**
           不列出「店铺优惠」的话，用户不知道自己因为挂在这个商家名下省了多少 ——
           而那正是他不去别处买的理由。

        ⚠️ 「店铺优惠」和「优惠券」是**两回事**，必须分开列：
           前者是"这家店本来就卖得比平台指导价便宜"，后者是在此之上再减。
           合成一个数的话用户搞不清哪部分是店铺给的、哪部分是券给的。
      */}
      <section className="mt-2 bg-white px-4 py-3">
        <h2 className="mb-1 text-sm font-medium">金额明细</h2>

        {/* ⚠️ 没有店铺优惠时（游客、或商家没让利）整块原价行都不显示 ——
             划一个跟实付一样的数只会让人困惑 */}
        {cart.totalShopDiscount > 0 && (
          <>
            <Row label="商品原价">
              <Price cents={cart.totalOriginal} className="text-muted-foreground" />
            </Row>
            <Row label="店铺优惠">
              <span className="text-brand-600">
                −<Price cents={cart.totalShopDiscount} />
              </span>
            </Row>
          </>
        )}

        {cart.totalShopDiscount === 0 && (
          <Row label="商品金额">
            <Price cents={cart.totalGoods} />
          </Row>
        )}

        <Row label="运费">
          {cart.totalFreight > 0 ? <Price cents={cart.totalFreight} /> : <span>免运费</span>}
        </Row>

        {c.coupon && (
          <Row label="优惠券">
            <span className="text-brand-600">
              −<Price cents={c.coupon.discount} />
            </span>
          </Row>
        )}

        <div className="mt-1 flex items-baseline justify-between border-t border-border pt-2.5">
          <span className="text-[14px] font-medium">实付</span>
          <Price cents={c.payable} className="text-[20px] text-brand-600" />
        </div>
      </section>

      {cart.groups.length > 1 && (
        <p className="px-4 py-2 text-xs text-ink-500">
          来自 {cart.groups.length} 个供应商，将拆成 {cart.groups.length} 张订单分别发货
        </p>
      )}

      {/* ── 优惠券 ── */}
      <section className="mt-2 bg-white px-4 py-3">
        <p className="mb-2 text-sm">优惠券</p>
        {c.coupon ? (
          <div className="flex items-center gap-3 rounded-lg bg-brand-50 px-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-brand-700">{c.coupon.name}</p>
              <p className="text-xs text-brand-600">
                减 <Price cents={c.coupon.discount} />
                {/*
                  🔴 拆单时必须说清楚券用在哪一单上，
                     否则用户会以为整单都减了，看到订单金额会觉得被多扣了。
                */}
                {cart.groups.length > 1 && (
                  <span className="ml-1 text-ink-500">
                    （用于「
                    {cart.groups.find((g) => g.supplierOrgId === c.coupon!.supplierOrgId)
                      ?.supplierName ?? "其中一"}
                    」那一单）
                  </span>
                )}
              </p>
            </div>
            <button
              className="shrink-0 text-xs text-ink-500"
              onClick={() => {
                c.clearCoupon();
                setCouponInput("");
              }}
            >
              不用了
            </button>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <Input
                placeholder="输入券码"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                className="flex-1"
              />
              <Button
                variant="outline"
                loading={c.preview.isPending}
                disabled={!couponInput}
                onClick={() => c.applyCoupon(couponInput)}
              >
                使用
              </Button>
            </div>
            {c.couponError && <p className="mt-1.5 text-xs text-red-600">{c.couponError}</p>}
          </>
        )}
      </section>

      <section className="mt-2 bg-white px-4 py-3">
        <Input
          label="订单备注"
          placeholder="如：请工作日送货、需要送到 8 楼"
          value={c.note}
          onChange={(e) => c.setNote(e.target.value)}
        />
      </section>
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与逻辑两边共用）。 */
function CheckoutRoute() {
  return useIsDesktop() ? <DesktopCheckout /> : <Checkout />;
}

export const checkoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/checkout",
  component: CheckoutRoute,
  /**
   * 🔴 **「立即下单」把要买的那一件放在地址栏上，不放全局状态。**
   *    放 state 的话刷新一次就没了，用户回到结算页看到的是**购物车的内容** ——
   *    他以为在买这一件，实际下的是另一批货。地址栏是刷新后还在的那一份。
   */
  validateSearch: (s: Record<string, unknown>): { skuId?: number; qty?: number } => ({
    skuId: s.skuId ? Number(s.skuId) : undefined,
    qty: s.qty ? Number(s.qty) : undefined,
  }),
});
